import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import useImageUrl from '../../../hooks/image/useImageUrl';
import FullProduct from '../../../models/product/FullProduct';
import { modelOf } from '../../../prop-types';
import LocationContext from '../../../services/LocationContext';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import CurrencyStore from '../../../store/CurrencyStore';
import AlternateLink from '../../head/AlternateLink';
import CanonicalLink from '../../head/CanonicalLink';
import GenericMeta from '../../head/GenericMeta';
import OGPProduct from '../../head/OGPProduct';
import RobotsMeta from '../../head/RobotsMeta';
import SEODescription from '../../head/SEODescription';
import SEOTitle from '../../head/SEOTitle';
import ProductClass from "../../../types/ProductClass";
import ProductGroupSchema from "../../schema/product/ProductGroupSchema";
import ProductSchema from "../../schema/product/ProductSchema";

const ProductPageHead = ({
  accountStore,
  currencyStore,
  product,
  routeService,
  locationContext,
  activeProductId,
}) => {
  const media = useImageUrl({ product, locationContext });

  const actualProduct = product.getActualProduct(activeProductId) || product;
  const canonicalPath = routeService.getCanonicalProductPath(product);

  const getItemGroupId = () => {
    if (product.isMulti()) {
      return product.id;
    }

    return null;
  };

  const getSchema = () => {
    if (product.class === ProductClass.MULTI) {
      return <ProductGroupSchema product={product} />;
    }

    return <ProductSchema product={product} activeProductId={activeProductId} />;
  };

  return (
    <>
      {!product.isIndexableByRobots && <RobotsMeta noindex nofollow />}
      <GenericMeta name="twitter:card" content="summary" />
      {media && (
        <>
          <GenericMeta name="twitter:image" content={media} />
          <GenericMeta property="og:image" content={media} />
        </>
      )}
      <GenericMeta property="og:type" content="product" />
      <OGPProduct
        price={actualProduct?.getPriceWithPrecision(
          accountStore.showPricesWithTax
        )}
        availability={actualProduct.og_availability}
        manufacturer={product.manufacturer_id}
        currency={currencyStore.activeCurrency.code}
        retailerItemId={
          actualProduct.manufacturer_product_id || actualProduct.id
        }
        itemGroupId={getItemGroupId()}
      />
      <SEOTitle title={product.seoTitle} />
      {product.seoDescription && (
        <SEODescription content={product.seoDescription} />
      )}
      <CanonicalLink path={canonicalPath} />
      <AlternateLink isProductPage model={actualProduct} />
      {getSchema()}
    </>
  );
};

ProductPageHead.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  locationContext: PropTypes.instanceOf(LocationContext).isRequired,
  product: modelOf(FullProduct).isRequired,
  activeProductId: PropTypes.string,
};

export default inject(
  'accountStore',
  'currencyStore',
  'routeService',
  'locationContext'
)(observer(ProductPageHead));
